import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest } from '../../services/api/apiService';

// ── Small reusable components ─────────────────────────────────────

const KpiCard = ({ label, value, sub, color, icon, dense = false }) => (
  <div className={`rounded-2xl p-4 md:p-5 ${color} flex flex-col gap-1.5`}>
    <div className="flex items-center justify-between gap-2">
      <p className="text-xs font-semibold uppercase tracking-wide opacity-70 min-w-0 leading-tight">{label}</p>
      {icon && <span className="text-lg opacity-40 flex-shrink-0">{icon}</span>}
    </div>
    {/* dense = tighter font ramp for crowded rows (e.g. 5-col with large numeric values) */}
    <p className={`${dense ? 'text-base md:text-lg lg:text-xl' : 'text-base md:text-xl lg:text-3xl'} font-black leading-none break-words`}>{value}</p>
    {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
  </div>
);

const Bar = ({ value, max, color }) => (
  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
    <div
      className={`h-full rounded-full transition-all duration-700 ${color}`}
      style={{ width: max > 0 ? `${Math.min((value / max) * 100, 100)}%` : '0%' }}
    />
  </div>
);

const PLATFORM_META = {
  meta:     { label: 'Meta',     color: 'bg-blue-500',   badge: 'bg-blue-100 text-blue-700'   },
  tiktok:   { label: 'TikTok',   color: 'bg-pink-500',   badge: 'bg-pink-100 text-pink-700'   },
  google:   { label: 'Google',   color: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-700'},
  snapchat: { label: 'Snapchat', color: 'bg-yellow-400', badge: 'bg-yellow-50 text-yellow-600' },
  linkedin: { label: 'LinkedIn', color: 'bg-sky-600',    badge: 'bg-sky-100 text-sky-700'     },
};

const fmt = {
  currency: (v) => `€${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  number:   (v) => Number(v || 0).toLocaleString('en-US'),
  pct:      (v) => `${Number(v || 0).toFixed(2)}%`,
};

const PERIOD_OPTIONS = [
  { value: '7d',  label: '7 days'  },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

// ── Main component ────────────────────────────────────────────────
const Statistics = () => {
  const navigate = useNavigate();
  const [overview, setOverview]   = useState(null);
  const [adPerf, setAdPerf]       = useState(null);
  const [period, setPeriod]       = useState('30d');
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingAd, setLoadingAd] = useState(true);

  // Load platform-agnostic overview (rules, wallet, etc.)
  useEffect(() => {
    getRequest('/api/statistics')
      .then(setOverview)
      .catch(() => {})
      .finally(() => setLoadingOverview(false));
  }, []);

  // Load real ad performance — re-fetches when period changes
  useEffect(() => {
    setLoadingAd(true);
    setAdPerf(null);
    getRequest(`/api/statistics/ad-performance?period=${period}`)
      .then(setAdPerf)
      .catch(() => {})
      .finally(() => setLoadingAd(false));
  }, [period]);

  const isLoading = loadingOverview || loadingAd;

  if (isLoading && !overview && !adPerf) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
        Loading statistics…
      </div>
    );
  }

  const ov  = overview?.overview   || {};
  const rs  = overview?.ruleStats  || {};
  const spendTrend = overview?.spendTrend || [];
  const logTrend   = overview?.logTrend   || [];
  const platformBreakdown = overview?.platformBreakdown || [];

  const totals     = adPerf?.totals     || {};
  const byPlatform = adPerf?.byPlatform || [];
  const errors     = adPerf?.errors     || {};

  const successPct = rs.total > 0 ? Math.round((rs.successCount / rs.total) * 100) : 0;
  const maxSpend   = Math.max(...spendTrend.map((s) => s.amount), 1);
  const maxLogs    = Math.max(...logTrend.map((l) => l.total), 1);
  const maxPlatform = Math.max(...platformBreakdown.map((p) => p.count), 1);
  const maxPlatformSpend = Math.max(...byPlatform.map((p) => p.spend || 0), 1);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-8">

      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-[#221C51]">Statistics</h2>

        {/* Period selector */}
        <div className="flex items-center bg-gray-100 rounded-xl p-1 text-xs font-semibold gap-0.5">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className={`px-4 py-1.5 rounded-lg transition-all ${
                period === opt.value
                  ? 'bg-[#221C51] text-white shadow'
                  : 'text-gray-500 hover:text-[#221C51]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Getting Started banner (shown when no platforms connected) ── */}
      {overview && ov.connectedPlatforms === 0 && (
        <div className="rounded-2xl border-2 border-dashed border-[#221C51]/20 bg-gradient-to-r from-indigo-50 to-purple-50 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="text-4xl">🚀</div>
          <div className="flex-1">
            <h3 className="font-bold text-[#221C51] text-base mb-1">Get started by connecting a platform</h3>
            <p className="text-sm text-gray-500 leading-relaxed">
              Link Meta, TikTok, Google, Snapchat or LinkedIn to create your first campaigns and see your real statistics here.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/dashboard/connected-platforms')}
              className="px-5 py-2.5 bg-[#221C51] text-white text-sm font-semibold rounded-xl hover:bg-[#2d2570] transition-colors shadow-sm"
            >
              Connect a platform →
            </button>
            <button
              onClick={() => navigate('/dashboard/create-ad-ai')}
              className="px-5 py-2.5 bg-white text-[#221C51] text-sm font-semibold rounded-xl border border-[#221C51]/20 hover:bg-gray-50 transition-colors"
            >
              Create with AI
            </button>
          </div>
        </div>
      )}

      {/* ── Real Ad Performance KPIs ── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide">
            Ad performance — {PERIOD_OPTIONS.find(o => o.value === period)?.label}
          </h3>
          {loadingAd && (
            <span className="text-xs text-gray-400 animate-pulse">Refreshing…</span>
          )}
        </div>

        {byPlatform.length === 0 && !loadingAd ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center text-gray-400 text-sm">
            No ad data available for the selected period.
            {Object.keys(errors).length > 0 && (
              <div className="mt-3 space-y-1">
                {Object.entries(errors).map(([p, msg]) => (
                  <p key={p} className="text-xs text-red-400">
                    {PLATFORM_META[p]?.label || p}: {msg}
                  </p>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Top KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <KpiCard dense label="Total spend"         value={fmt.currency(totals.spend)}       color="bg-[#221C51] text-white"      icon="💸" />
              <KpiCard dense label="Impressions"         value={fmt.number(totals.impressions)}   color="bg-indigo-50 text-[#221C51]"  icon="👁" />
              <KpiCard dense label="Clicks"              value={fmt.number(totals.clicks)}        color="bg-blue-50 text-blue-900"     icon="🖱" />
              <KpiCard dense label="Avg. CTR"            value={fmt.pct(totals.ctr)}              color="bg-green-50 text-green-800"   icon="📈" />
              <KpiCard dense label="Avg. CPC"            value={fmt.currency(totals.cpc)}         color="bg-yellow-50 text-yellow-800" icon="🎯" />
            </div>

            {/* Platform breakdown table */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50">
                <h4 className="text-xs font-bold text-[#221C51] uppercase tracking-wide">Breakdown by platform</h4>
              </div>
              <div className="divide-y divide-gray-50">
                {byPlatform
                  .sort((a, b) => (b.spend || 0) - (a.spend || 0))
                  .map((p) => {
                    const meta = PLATFORM_META[p.platform] || { label: p.platform, color: 'bg-gray-400', badge: 'bg-gray-100 text-gray-700' };
                    const ctr = p.impressions > 0 ? ((p.clicks / p.impressions) * 100).toFixed(2) : '0.00';
                    const cpc = p.clicks > 0 ? (p.spend / p.clicks).toFixed(2) : '0.00';
                    return (
                      <div key={p.platform} className="px-5 py-4 hover:bg-gray-50 transition">
                        <div className="flex items-center gap-4 mb-2">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.badge}`}>
                            {meta.label}
                          </span>
                          <span className="ml-auto text-sm font-bold text-[#221C51]">{fmt.currency(p.spend)}</span>
                        </div>
                        <Bar value={p.spend} max={maxPlatformSpend} color={meta.color} />
                        <div className="flex gap-6 mt-2 text-xs text-gray-500">
                          <span><span className="font-medium text-[#221C51]">{fmt.number(p.impressions)}</span> impressions</span>
                          <span><span className="font-medium text-[#221C51]">{fmt.number(p.clicks)}</span> clicks</span>
                          <span>CTR <span className="font-medium text-[#221C51]">{ctr}%</span></span>
                          <span>CPC <span className="font-medium text-[#221C51]">€{cpc}</span></span>
                          {p.reach > 0 && <span>Reach <span className="font-medium text-[#221C51]">{fmt.number(p.reach)}</span></span>}
                        </div>
                      </div>
                    );
                  })}
              </div>
              {/* Errors for platforms that failed */}
              {Object.keys(errors).length > 0 && (
                <div className="px-5 py-3 bg-red-50 border-t border-red-100">
                  <p className="text-xs text-red-500 font-semibold mb-1">Platforms unavailable:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(errors).map(([p, msg]) => (
                      <span key={p} className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                        {PLATFORM_META[p]?.label || p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* ── Platform-agnostic overview KPIs ── */}
      {overview && (
        <section>
          <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-3">Overview</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard label="Connected platforms" value={ov.connectedPlatforms}           color="bg-[#221C51] text-white"      icon="🔗" />
            <KpiCard label="Active rules"          value={`${ov.activeRules || 0} / ${ov.totalRules || 0}`} sub="rules configured" color="bg-indigo-50 text-[#221C51]" icon="⚡" />
            <KpiCard label="Rule executions"    value={ov.totalExecutions || 0}        sub={`${successPct}% success`}     color="bg-green-50 text-green-800"   icon="🤖" />
            <KpiCard label="Wallet"                  value={`€${ov.walletBalance || '0.00'}`} sub={ov.isPremium ? 'Premium plan ✓' : 'Free plan'} color="bg-yellow-50 text-yellow-800" icon="💼" />
          </div>
        </section>
      )}

      {/* ── Rule performance + Platform rule activity ── */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-4">Rule performance</h3>
            {!rs.total ? (
              <p className="text-xs text-gray-400 text-center py-6">No rule executions recorded.</p>
            ) : (
              <div className="space-y-4">
                {[
                  { label: 'Success', count: rs.successCount, pct: successPct, color: 'bg-green-500', text: 'text-green-600' },
                  { label: 'Failed', count: rs.failedCount,   pct: rs.total > 0 ? Math.round((rs.failedCount  / rs.total) * 100) : 0, color: 'bg-red-400',  text: 'text-red-500'  },
                  { label: 'Skipped', count: rs.skippedCount,  pct: rs.total > 0 ? Math.round((rs.skippedCount / rs.total) * 100) : 0, color: 'bg-gray-400', text: 'text-gray-500' },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className={`font-semibold ${row.text}`}>{row.label}</span>
                      <span className="text-gray-400">{row.count} ({row.pct}%)</span>
                    </div>
                    <Bar value={row.count} max={rs.total} color={row.color} />
                  </div>
                ))}
                <p className="text-xs text-gray-400 text-right pt-1">Total: {rs.total} executions</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-4">Rule activity by platform</h3>
            {platformBreakdown.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">No activity recorded.</p>
            ) : (
              <div className="space-y-3">
                {platformBreakdown.sort((a, b) => b.count - a.count).map((p) => (
                  <div key={p.platform}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold text-[#221C51] capitalize">{PLATFORM_META[p.platform]?.label || p.platform}</span>
                      <span className="text-gray-400">{p.count}</span>
                    </div>
                    <Bar value={p.count} max={maxPlatform} color={PLATFORM_META[p.platform]?.color || 'bg-gray-400'} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Monthly Spend Trend (wallet top-ups) ── */}
      {overview && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-5">
            Wallet top-ups — last 6 months
          </h3>
          {spendTrend.every((s) => s.amount === 0) ? (
            <p className="text-xs text-gray-400 text-center py-6">No payments in the last 6 months.</p>
          ) : (
            <div className="flex items-end gap-3 h-32">
              {spendTrend.map((s) => (
                <div key={s.month} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-semibold text-[#221C51]">{s.amount > 0 ? `€${s.amount}` : ''}</span>
                  <div className="w-full flex items-end" style={{ height: '80px' }}>
                    <div
                      className="w-full rounded-t-md bg-[#221C51] transition-all duration-700"
                      style={{ height: `${maxSpend > 0 ? Math.max((s.amount / maxSpend) * 80, s.amount > 0 ? 6 : 0) : 0}px` }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 text-center leading-tight">{s.month}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Monthly Rule Execution Trend ── */}
      {overview && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-bold text-[#221C51] uppercase tracking-wide mb-5">
            Rule executions — last 6 months
          </h3>
          {logTrend.every((l) => l.total === 0) ? (
            <p className="text-xs text-gray-400 text-center py-6">No executions in the last 6 months.</p>
          ) : (
            <>
              <div className="flex items-end gap-3 h-32">
                {logTrend.map((l) => (
                  <div key={l.month} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs font-semibold text-[#221C51]">{l.total > 0 ? l.total : ''}</span>
                    <div className="w-full flex items-end flex-col-reverse gap-px" style={{ height: '80px' }}>
                      {[
                        { key: 'success', color: 'bg-green-500' },
                        { key: 'failed',  color: 'bg-red-400'   },
                        { key: 'skipped', color: 'bg-gray-300'  },
                      ].map(({ key, color }) => (
                        <div
                          key={key}
                          className={`w-full ${color} transition-all duration-700`}
                          style={{ height: `${maxLogs > 0 ? (l[key] / maxLogs) * 80 : 0}px` }}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-gray-400 text-center leading-tight">{l.month}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" /> Success</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-400   inline-block" /> Failed</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-gray-300  inline-block" /> Skipped</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Statistics;
